import React, { memo, useCallback, useState, useEffect } from "react";
import style from "./TeamMobile.module.scss";
import { TitleAnimation } from "@/components/common/TitleAnimation/TitleAnimation";
import { API_BASE } from "../../../../constants/config";
import { motion, AnimatePresence } from "framer-motion";
import { MagneticCursor } from "@/components/common/MagneticCursor/MagneticCursor";
import { Icon } from "@/components/common/Icons/Icon";
import { PixelEvents } from "@/components/Pixel/PixelManager";

const Card = memo(
  ({
    name,
    role,
    image,
    onClick,
  }: {
    name: string;
    role: string;
    image: string;
    onClick: () => void;
  }) => (
    <div className={style.card} onClick={onClick}>
      <div className={style.card_inner}>
        <div className={style.card_inner_front}>
          <div className={style.card_information}>
            <h2>{name}</h2>
            <p>{role}</p>
          </div>
          <picture className={style.card_image}>
            <img src={`${API_BASE}${image}`} alt={`${name} picture`} />
          </picture>
        </div>
        <div className={style.card_inner_back}>
          <h1>{name}</h1>
        </div>
      </div>
      <div className={style.card_glow}></div>
    </div>
  )
);
Card.displayName = "Card";

const CardPopup = memo(
  ({
    card,
    onClose,
  }: {
    card: {
      name: string;
      rol: string;
      linkedin?: string;
      description?: string;
    };
    onClose: () => void;
  }) => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={style.cardPopup}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className={style.content}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={style.infoContainer}>
          <h2 className={style.name}>{card.name}</h2>
          <p className={style.role}>{card.rol}</p>
          {card.linkedin && (
            <MagneticCursor>
              <a 
                href={card.linkedin} 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={() => PixelEvents.clickLinkedInProfile(card.name, card.rol)}
              >
                <Icon name="LinkedIn" fill="var(--secondary)" />
              </a>
            </MagneticCursor>
          )}
          <p className={style.description}>{card.description || " "}</p>
        </div>
      </motion.div>
    </motion.div>
  )
);
CardPopup.displayName = "CardPopup";

export default function TeamMobile({ messages }) {
  const sliceTeam = messages?.team?.slice(6);
  const [selectedCard, setSelectedCard] = useState(null);

  const handleCardClick = useCallback((item) => {
    setSelectedCard(item);
  }, []);

  const handleClosePopup = useCallback(() => {
    setSelectedCard(null);
  }, []);

  useEffect(() => {
    if (selectedCard) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [selectedCard]);

  if (!sliceTeam) return null;

  return (
    <div className={style.section}>
      <div className={style.container}>
        <div className="container">
          <TitleAnimation
            messages={{
              first_word: messages.first_word_all,
              second_word: messages.second_word_all,
            }}
          />
        </div>
        <div className={style.cardsContainer}>
          {sliceTeam.map((item) => (
            <Card
              key={item.id || item.name}
              name={item.name}
              role={item.rol}
              image={item.horizontal_image}
              onClick={() => handleCardClick(item)}
            />
          ))}
        </div>
      </div>
      <AnimatePresence>
        {selectedCard && (
          <CardPopup card={selectedCard} onClose={handleClosePopup} />
        )}
      </AnimatePresence>
    </div>
  );
}
